import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CACHE_TIME,
  type ReopenedPeriodItem,
  type TimeReportConfig,
  type TimeReportData,
} from '@hygge/shared';
import { unsavedTimerMessage } from '@/features/time-tracker/lib/unsaved-timer';
import {
  ENTRY_DELETE_KEY,
  ENTRY_UPDATE_KEY,
  refreshAfterEntryWrite,
} from '@/features/time-tracker/lib/entry-saves';
import { reportWithoutEntry, reportWithSavedEntry } from '@/features/time-tracker/lib/report-rows';
import {
  liveReopenSignature,
  REOPEN_EXPIRY_RECHECK_MS,
  msUntilNextReopenExpiry,
  reopenExpiryTimerMs,
} from '@/features/time-tracker/lib/reopen-window';
import {
  getTimeEntries,
  getAllTimeEntries,
  createTimeEntry,
  updateTimeEntry,
  deleteTimeEntry,
  getTimeSummary,
  getAllTeamTimeEntries,
  getTeamTimeSummary,
  getTimePeriodPresets,
  getProjectTimeEntries,
  getProjectTimeStats,
  getTaskTimeEntries,
  getLoggableEmployees,
  getOwnerProjects,
  getMyReopenedPeriods,
  getProjectTasks,
  getReportData,
  getCurrentTimer,
  startTimer,
  stopTimer,
  runTimeReport,
  draftReportConfig,
  getSavedReports,
  createSavedReport,
  updateSavedReport,
  duplicateSavedReport,
  deleteSavedReport,
  type SavedReportScope,
} from '@/lib/time-entries';
import { viewerTimeZone } from '@/lib/timezone';
import { useAuthStore } from '@/stores/auth';
import { useTimerStore } from '@/stores/timer';

// ─── Query Keys ────────────────────────────────────────────────────────────

export const timeEntryKeys = {
  all: ['time-entries'] as const,
  // Outside `all`: writing an entry opens or closes no window. The viewer is
  // part of the key — the answer is the caller's own. [HW-963]
  reopenedPeriods: (viewerId: string) => ['time-reopened-periods', viewerId] as const,
  lists: () => [...timeEntryKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...timeEntryKeys.lists(), filters] as const,
  summaries: () => [...timeEntryKeys.all, 'summary'] as const,
  summary: (from: string, to: string) => [...timeEntryKeys.summaries(), from, to] as const,
  team: (filters?: Record<string, unknown>) => [...timeEntryKeys.all, 'team', filters] as const,
  teamSummary: (from: string, to: string, projectId?: string) =>
    [...timeEntryKeys.all, 'team-summary', from, to, projectId ?? null] as const,
  project: (projectId: string, filters?: Record<string, unknown>) => [...timeEntryKeys.all, 'project', projectId, filters] as const,
  projectStats: (projectId: string) => [...timeEntryKeys.all, 'project-stats', projectId] as const,
  task: (taskId: string) => [...timeEntryKeys.all, 'task', taskId] as const,
  /** Every cached `useTimeReport` read, whatever its parameters. [HW-974] */
  reports: () => [...timeEntryKeys.all, 'report'] as const,
  loggableEmployees: (projectId: string) =>
    [...timeEntryKeys.all, 'loggable-employees', projectId] as const,
  savedReports: () => [...timeEntryKeys.all, 'saved-reports'] as const,
  // Outside `all`: an entry written or deleted does not move the calendar.
  periodPresets: () => ['time-period-presets'] as const,
  // Outside `all` too: writing an entry does not change a project's tasks, so
  // a save has no reason to refetch them. [HW-974]
  projectTasks: (projectId: string) => ['time-entry-project-tasks', projectId] as const,
  // Outside `all` for the same reason: an entry written moves nobody's
  // memberships. [HW-974]
  // The viewer is part of the key: the answer is narrowed by who asks (a
  // manager sees only the owner's projects they manage), so a list cached for
  // one viewer must not serve another after an impersonation switch. [HW-974]
  ownerProjects: (employeeId: string, viewerId: string) =>
    ['time-entry-owner-projects', employeeId, viewerId] as const,
};

export const timerKeys = {
  current: ['timer', 'current'] as const,
};

// ─── Time Entries ──────────────────────────────────────────────────────────

export function useTimeEntries(filters?: { from?: string; to?: string; projectId?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: timeEntryKeys.list(filters),
    queryFn: () => getTimeEntries(filters),
    staleTime: CACHE_TIME.SHORT,
  });
}

/**
 * Every entry in a window across all its pages — see `getAllTimeEntries`. Keyed
 * under `timeEntryKeys.all` so a create, edit or delete refetches it. [HW-869]
 */
export function useAllTimeEntries(filters?: { from?: string; to?: string; projectId?: string }) {
  return useQuery({
    queryKey: [...timeEntryKeys.all, 'all', filters],
    queryFn: () => getAllTimeEntries(filters),
    staleTime: CACHE_TIME.SHORT,
  });
}

/**
 * `suppressGlobalToast`: Log Time prints its own refusals, inside the dialog
 * the Save is in.
 *
 * The server can refuse a perfectly typed entry — the day is already full
 * [HW-859], the month is closed [HW-846], the project is not the caller's to
 * log against — and the answer is a sentence about the form still on screen.
 * In the corner of the page it competed with a dialog that looked untouched;
 * beside the button it explains why nothing happened. Marked here rather than
 * handled here because the dialogs report by RENDERING `isError`, which the
 * global MutationCache cannot see — without this flag it would add a toast
 * saying the same thing again. The dialogs pay for the flag by catching the
 * rejection themselves when they are unmounted mid-save; see either of them.
 */
export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createTimeEntry,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntryKeys.all });
    },
  });
}

/**
 * Printed inside Edit Time Entry, for the reasons on `useCreateTimeEntry`.
 *
 * `untilRefetched` keeps the save pending until the refetch it starts has
 * landed (a returned `onSuccess` promise is awaited before the mutation
 * settles). Show entries asks for it: its row stays locked while pending, so
 * a pencil or a cell opened next is seeded from the saved entry, not from the
 * row as it was before the save. Keyed, so the row finds every save of its
 * entry — its cells' and the Edit dialog's (`entryWriteFilter`).
 *
 * The cached reports take the saved entry from the server's answer before the
 * refetch (`reportWithSavedEntry`). The refetch's own failure is swallowed —
 * `invalidateQueries` does not throw unless asked to (`throwOnError`), and a
 * failed query keeps its old data — so without this the row would unlock on
 * the entry from before the save, and a note-only Save from its pencil would
 * send the old length back. [HW-974]
 */
export function useUpdateTimeEntry({ untilRefetched = false }: { untilRefetched?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ENTRY_UPDATE_KEY,
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      updateTimeEntry(id, payload),
    meta: { suppressGlobalToast: true },
    onSuccess: (saved) => {
      qc.setQueriesData<TimeReportData>({ queryKey: timeEntryKeys.reports() }, (report) =>
        reportWithSavedEntry(report, saved)
      );
      const refetched = refreshAfterEntryWrite(qc);
      return untilRefetched ? refetched : undefined;
    },
  });
}

/**
 * Keyed and held like `useUpdateTimeEntry`: with `untilRefetched` the delete
 * stays pending until the refetch has landed, so the Show entries row that
 * sent it stays shut (`entryWriteFilter`) and its confirmation stays on
 * "Deleting..." — no second Delete reaches a 404. The cached reports drop
 * the entry first (`reportWithoutEntry`), so a refetch that fails does not
 * bring the row back. [HW-974]
 */
export function useDeleteTimeEntry({ untilRefetched = false }: { untilRefetched?: boolean } = {}) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ENTRY_DELETE_KEY,
    mutationFn: deleteTimeEntry,
    onSuccess: (_answer, id) => {
      qc.setQueriesData<TimeReportData>({ queryKey: timeEntryKeys.reports() }, (report) =>
        reportWithoutEntry(report, id)
      );
      const refetched = refreshAfterEntryWrite(qc);
      return untilRefetched ? refetched : undefined;
    },
  });
}

// ─── Summary ───────────────────────────────────────────────────────────────

export function useTimeSummary(from: string | undefined, to: string | undefined) {
  return useQuery({
    queryKey: timeEntryKeys.summary(from ?? '', to ?? ''),
    queryFn: () => getTimeSummary(from!, to!),
    enabled: !!from && !!to,
    staleTime: CACHE_TIME.SHORT,
  });
}

// ─── Team ──────────────────────────────────────────────────────────────────

/**
 * Every team entry in a window across all its pages — see
 * `getAllTeamTimeEntries`. Under `timeEntryKeys.all` like the rest, so a
 * create, edit or delete refetches it. [HW-930]
 */
export function useAllTeamTimeEntries(filters?: { from?: string; to?: string; projectId?: string; employeeId?: string }) {
  return useQuery({
    queryKey: [...timeEntryKeys.team(filters), 'all'],
    queryFn: () => getAllTeamTimeEntries(filters),
    staleTime: CACHE_TIME.SHORT,
  });
}

/**
 * Which days each period preset names today, on the company calendar.
 *
 * Not polled, nor timed to the company's midnight: the answer changes only at
 * the midnight into a Monday or a 1st. It is fetched on mount (fresh for a
 * minute) and asked again when the reader acts — on every return to the tab or
 * the window, and when they change the period or the project
 * (`useRefreshTimePeriods`) — so a page left idle across that midnight shows
 * the previous period, consistently on every half, only until they come back
 * to it. [HW-929]
 */
export function useTimePeriodPresets() {
  const queryClient = useQueryClient();
  // React Query refetches on focus only when the TAB was hidden
  // (`visibilitychange`). A page that stayed visible — a second monitor, a
  // window that was never minimised — gets nothing when the reader clicks back
  // into it, so the window's own focus asks again too. Only this tiny query;
  // `cancelRefetch: false` joins a request already in flight rather than
  // doubling it when both events fire.
  useEffect(() => {
    const onFocus = () =>
      void queryClient.refetchQueries(
        { queryKey: timeEntryKeys.periodPresets(), type: 'active' },
        { cancelRefetch: false }
      );
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [queryClient]);
  return useQuery({
    queryKey: timeEntryKeys.periodPresets(),
    queryFn: getTimePeriodPresets,
    staleTime: CACHE_TIME.SHORT,
    refetchOnWindowFocus: 'always',
  });
}

/**
 * Asks the company calendar again — called when the reader changes the period
 * or the project, so a page that sat idle across the company's midnight moves
 * to the new period when they act on it. One tiny request per change. [HW-929]
 */
export function useRefreshTimePeriods() {
  const queryClient = useQueryClient();
  return useCallback(
    // Joins a request already in flight (a focus refetch) rather than
    // cancelling it and sending a second one.
    () =>
      queryClient.invalidateQueries(
        { queryKey: timeEntryKeys.periodPresets() },
        { cancelRefetch: false }
      ),
    [queryClient]
  );
}

/**
 * The Dashboard tab's summary. Idle until both ends of the period are known —
 * a custom range is half-typed for as long as it takes to pick the second date,
 * and half a window is not a question worth asking the server.
 */
export function useTeamTimeSummary(
  from: string | undefined,
  to: string | undefined,
  projectId?: string,
) {
  return useQuery({
    queryKey: timeEntryKeys.teamSummary(from ?? '', to ?? '', projectId),
    queryFn: () => getTeamTimeSummary({ from: from!, to: to!, ...(projectId ? { projectId } : {}) }),
    enabled: !!from && !!to,
    staleTime: CACHE_TIME.SHORT,
    // A 4xx here is the window itself being wrong, and the server already said
    // how in a message the screen shows. Retrying that three times only delays
    // the sentence; anything else still gets the usual couple of attempts.
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) return false;
      return failureCount < 2;
    },
  });
}

// ─── Project ───────────────────────────────────────────────────────────────

export function useProjectTimeEntries(projectId: string | undefined, filters?: { from?: string; to?: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: timeEntryKeys.project(projectId ?? '', filters),
    queryFn: () => getProjectTimeEntries(projectId!, filters),
    enabled: !!projectId,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useProjectTimeStats(projectId: string | undefined) {
  return useQuery({
    queryKey: timeEntryKeys.projectStats(projectId ?? ''),
    queryFn: () => getProjectTimeStats(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

// ─── Task ──────────────────────────────────────────────────────────────────

export function useTaskTimeEntries(taskId: string | undefined) {
  return useQuery({
    queryKey: timeEntryKeys.task(taskId ?? ''),
    queryFn: () => getTaskTimeEntries(taskId!),
    enabled: !!taskId,
    staleTime: CACHE_TIME.SHORT,
  });
}

/**
 * Whose hours the caller may log on `projectId`. Keyed by the project, because
 * the authority is the project's — changing the project in the dialog asks the
 * question again rather than reusing the previous answer. [HW-864]
 */
export function useLoggableEmployees(projectId: string | undefined) {
  return useQuery({
    queryKey: timeEntryKeys.loggableEmployees(projectId ?? ''),
    queryFn: () => getLoggableEmployees(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * The open tasks of `projectId`, for the Edit dialog's Task select. Keyed by
 * the project, so changing the project in the dialog asks again. [HW-974]
 */
export function useProjectTasks(projectId: string | undefined) {
  return useQuery({
    queryKey: timeEntryKeys.projectTasks(projectId ?? ''),
    queryFn: () => getProjectTasks(projectId!),
    enabled: !!projectId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * The projects the Edit dialog's Project select offers for an entry of
 * `employeeId`: the owner's active projects, not the viewer's — an entry
 * stays on its owner's projects (2026-10-09 decision). [HW-974]
 */
export function useOwnerProjects(employeeId: string | undefined) {
  const viewerId = useAuthStore((s) => s.user?.id ?? '');
  return useQuery({
    queryKey: timeEntryKeys.ownerProjects(employeeId ?? '', viewerId),
    queryFn: () => getOwnerProjects(employeeId!),
    enabled: !!employeeId,
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * The caller's live reopened closed periods. Refetched every minute, so a
 * window granted while My Time is open appears without a reload; one that
 * expires is dropped by the notice's own clock before that. [HW-963]
 */
export function useMyReopenedPeriods() {
  const viewerId = useAuthStore((s) => s.user?.id ?? '');
  return useQuery({
    queryKey: timeEntryKeys.reopenedPeriods(viewerId),
    queryFn: getMyReopenedPeriods,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

/**
 * A time entry's edit, delete and Duplicate flags are the server's answer at
 * read time, so a reopened closed period that appears, is replaced, is
 * withdrawn or expires leaves the cached rows wrong: no pencil on a row just
 * opened, a pencil whose Save is refused once it lapses. Each change to the
 * viewer's live windows refetches the entries, and the soonest expiry is timed
 * rather than left to the next poll. The first answer seen only records the
 * set — the rows were read alongside it. [HW-963]
 */
export function useEntriesFollowReopenedPeriods(periods: ReopenedPeriodItem[] | undefined) {
  const qc = useQueryClient();
  const seen = useRef<string | null>(null);
  const recheck = useRef<number | undefined>(undefined);
  const [tick, setTick] = useState(0);
  useEffect(() => () => window.clearTimeout(recheck.current), []);
  useEffect(() => {
    if (!periods) return;
    const now = new Date();
    const signature = liveReopenSignature(periods, now);
    if (seen.current !== null && seen.current !== signature) {
      qc.invalidateQueries({ queryKey: timeEntryKeys.all });
    }
    seen.current = signature;
    const ms = msUntilNextReopenExpiry(periods, now);
    if (ms === null) return;
    // The viewer's clock may run ahead of the server's, which would still count
    // the window live at the first re-read, so the timer re-reads
    // unconditionally and once more after a margin; the periods list refreshing
    // in between must not cancel the second.
    const id = window.setTimeout(() => {
      qc.invalidateQueries({ queryKey: timeEntryKeys.all });
      setTick((n) => n + 1);
      window.clearTimeout(recheck.current);
      recheck.current = window.setTimeout(
        () => qc.invalidateQueries({ queryKey: timeEntryKeys.all }),
        REOPEN_EXPIRY_RECHECK_MS
      );
    }, reopenExpiryTimerMs(ms));
    return () => window.clearTimeout(id);
  }, [periods, tick, qc]);
}

// ─── Reports ──────────────────────────────────────────────────────────

export function useTimeReport(params: {
  groupBy: 'project' | 'employee';
  from: string;
  to: string;
  projectId?: string;
  employeeId?: string;
} | null) {
  return useQuery({
    queryKey: [...timeEntryKeys.reports(), params],
    queryFn: () => getReportData(params!),
    enabled: !!params,
    staleTime: CACHE_TIME.SHORT,
  });
}

// ─── The report builder ────────────────────────────────────────────────────

/**
 * Running a built report is a MUTATION, not a query.
 *
 * Not because it writes — it reads only — but because of when it happens: a
 * query keyed on the configuration would re-run itself every time a filter
 * moved, and the builder's whole contract is that nothing runs until Run is
 * pressed. That matters most right after the AI draft, where a form full of
 * proposed settings must sit there for review rather than fetch numbers.
 */
export function useRunTimeReport() {
  return useMutation({
    mutationFn: (config: TimeReportConfig) => runTimeReport(config),
    // The window guard's message ("more than 400 days", "from is after to") is
    // written for the reader and shown inline beside the form; the global
    // toast would repeat it in a corner.
    meta: { suppressGlobalToast: true },
  });
}

/**
 * A built report that runs itself — the Reports screen's summarized shapes.
 *
 * The Builder runs on a button, so it uses the mutation above; the Reports
 * screen has always shown its report the moment a control changes, and its
 * three presets are meant as one click, so here the same POST is a query keyed
 * by the configuration. Same endpoint, same aggregation, same rows — only the
 * trigger differs. A `null` configuration (a half-filled custom range) runs
 * nothing rather than sending a request the server would refuse. [HW-877]
 */
export function useBuiltReport(config: TimeReportConfig | null) {
  return useQuery({
    queryKey: [...timeEntryKeys.all, 'report-run', config],
    queryFn: () => runTimeReport(config!),
    enabled: !!config,
    staleTime: CACHE_TIME.SHORT,
  });
}

/** A sentence in, a configuration out. It fills the form and runs nothing. */
export function useDraftReportConfig() {
  return useMutation({
    mutationFn: (description: string) => draftReportConfig(description),
    meta: { suppressGlobalToast: true },
  });
}

export function useSavedReports(enabled = true) {
  return useQuery({
    queryKey: timeEntryKeys.savedReports(),
    queryFn: getSavedReports,
    enabled,
    staleTime: CACHE_TIME.SHORT,
  });
}

export function useSaveReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSavedReport,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntryKeys.savedReports() });
    },
  });
}

export function useUpdateSavedReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...patch
    }: {
      id: string;
      name?: string;
      scope?: SavedReportScope;
      config?: TimeReportConfig;
    }) => updateSavedReport(id, patch),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntryKeys.savedReports() });
    },
  });
}

export function useDuplicateSavedReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => duplicateSavedReport(id),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntryKeys.savedReports() });
    },
  });
}

export function useDeleteSavedReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSavedReport(id),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: timeEntryKeys.savedReports() });
    },
  });
}

// ─── Timer ─────────────────────────────────────────────────────────────────

export function useCurrentTimer() {
  return useQuery({
    queryKey: timerKeys.current,
    queryFn: getCurrentTimer,
    staleTime: 0,
    refetchInterval: 30000, // refetch every 30s to stay in sync
  });
}

/**
 * Start a timer - and the only supported way to do it.
 *
 * The running clock is displayed from the Zustand store, so the store is
 * brought level here rather than by the caller: every screen that starts a
 * timer needs both halves, and the two that hand-rolled it were the two that
 * forgot the cache.
 */
export function useStartTimer() {
  const qc = useQueryClient();
  return useMutation({
    // The zone is added here rather than by each caller, so no screen that
    // starts a timer can forget it. [HW-916]
    mutationFn: (payload: { taskId?: string; projectId?: string }) =>
      startTimer({ ...payload, timezone: viewerTimeZone() }),
    onSuccess: (timer) => {
      useTimerStore.getState().applyTimer(timer);
      qc.invalidateQueries({ queryKey: timerKeys.current });
      // Starting stops whatever ran before it, and the server closes that timer
      // into a finished entry. Nothing was logged on this screen, and the lists
      // still gained a row.
      qc.invalidateQueries({ queryKey: timeEntryKeys.all });
      // ...unless that timer had crossed the monthly cutoff: the 200 then says
      // what it did not write, and this is the only place that answer can
      // reach the screen — the global toast speaks on errors alone. [HW-861]
      if (timer.autoStopUnsaved) toast.warning(unsavedTimerMessage(timer.autoStopUnsaved));
    },
  });
}

/** Stop the running timer, and refresh the lists the entry it wrote belongs in. */
export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => stopTimer({ timezone: viewerTimeZone() }),
    onSuccess: (result) => {
      useTimerStore.getState().reset();
      qc.invalidateQueries({ queryKey: timerKeys.current });
      qc.invalidateQueries({ queryKey: timeEntryKeys.all });
      // A stop that crossed the monthly cutoff still stopped the timer — the
      // 200 carries what was not written, and saying nothing here would be
      // HW-837's silent-loss bug all over again. [HW-861]
      if (result?.unsaved) toast.warning(unsavedTimerMessage(result.unsaved));
    },
  });
}
